import React, { useState, useEffect, useRef } from 'react';
import { text } from '../../data';
import { useProject } from '../../project-details/context/ProjectContext';
import { createProjectBearings } from '../../api/api';
import { BearingSaveItem } from '../../../components/bearing-search/types/types';
import ToggleButton from './ToggleButton';
import SaveToProjectDropDown from '../SaveToProjectDropDown';
import toast from 'react-hot-toast';
import { useAuthentication } from '../../context/AuthenticationContext';
import { saveDropdownSelectionAnalyticsHandler, saveNewProjectClickAnalyticsHandler, saveDropDownOpenAnalyticsHandler } from '../../analytics';

type SaveMultiBearingDropdownProps = {
	bearings: BearingSaveItem[];
	buttonStyle?: string;
	displayedUnit?: string;
	hideDropdown?: boolean;
	menuDirection?: 'up' | 'down';
	tableBodyRef?: React.RefObject<HTMLTableSectionElement>;
	gridViewRef?: React.RefObject<HTMLDivElement>;
};

const SaveMultiBearingDropdown: React.FC<SaveMultiBearingDropdownProps> = ({ displayedUnit, buttonStyle, hideDropdown, bearings, menuDirection = 'down', tableBodyRef, gridViewRef }) => {
	const { isLoggedIn, idToken } = useAuthentication();
	const { projects, refreshProjects } = useProject();
	const [ authenticated, setAuthenticated ] = useState<boolean>(false);
	const [ inactive, setInactive ] = useState<boolean>(false);
	const [ isLoading, setIsLoading ] = useState(false);
	const [ isOpen, setIsOpen ] = useState(false);
	const dropdownRef = useRef<HTMLDivElement | null>(null);

	const toggleMenu = (event) => {
		const bearingIds = bearings.map((bearingItem) => bearingItem.marketPlaceDescription);
		saveDropDownOpenAnalyticsHandler(event, dropdownRef, isOpen, bearingIds, 'Add to Project', 'bearing part');
		setIsOpen((prev) => ! prev);
	};
	useEffect(() => {
		if (isLoggedIn && !! idToken) {
			setAuthenticated(true);
		}
	}, [ isLoggedIn, idToken ]);

	useEffect(() => {
		if (bearings.length === 0) {
			setInactive(true);
		} else {
			setInactive(false);
		}
	}, [ bearings ]);

	/**
	 * Close dropdown if user clicks outside of dropdown container
	 */
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && ! dropdownRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener('click', handleClickOutside);
		}

		return () => document.removeEventListener('click', handleClickOutside);
	}, [ isOpen ]);

	const trackSaveDropdownSelection = (event) => {
		saveDropdownSelectionAnalyticsHandler(event, dropdownRef, 'Add to Project', 'bearing part');
	};

	const buildBearingsListString = (bearingList: BearingSaveItem[]) => {
		return bearingList.map((bearingItem) => bearingItem.marketPlaceDescription).join(',');
	};

	/**
	 * Refresh project data when a new project is created
	 *
	 * @param  createdProjectId
	 */
	const onNewProjectCreated = async (createdProjectId) => {
		saveNewProjectClickAnalyticsHandler(dropdownRef, 'Add to Project', 'bearing part');
		await saveMultiBearingsToProject(createdProjectId);
	};

	const resetSelectedBearings = () => {
		const triggerReset = ($targetRef) => {
			if ($targetRef?.current) {
				const selectedItems: NodeListOf<HTMLLabelElement> = $targetRef.current.querySelectorAll('.m-checkbox--add input:checked + label');
				selectedItems.forEach((bearingInput) => bearingInput.click());
			}
		};
		if (tableBodyRef?.current) {
			triggerReset(tableBodyRef);
		}

		if (gridViewRef?.current) {
			triggerReset(gridViewRef);
		}
	};

	const saveMultiBearingsToProject = async (projectId: string) => {
		if (! projectId || ! bearings) {
			return;
		}

		try {
			setIsLoading(true);
			const toastId = toast.loading('Loading...');

			const targetProject = projects.find((projectItem) => projectItem.projectId === projectId);

			const noConflictProjectBearings = bearings.filter((bearing) => {
				return ! targetProject?.bearings.some((existingBearing) => existingBearing.marketPlaceDescription === bearing.marketPlaceDescription);
			});

			const isLimitedSave = noConflictProjectBearings.length < bearings.length;

			const bearingsLength = noConflictProjectBearings.length;
			const multiBearingPayload = {
				projectId,
				displayedUnit,
				bearings: noConflictProjectBearings,
			};
			const exceptionSuccessMessage = isLimitedSave
				? `${ text.oneOrMoreItems }: ${ targetProject?.projectName || '' }. `
				: '';

			await createProjectBearings(multiBearingPayload);
			const pluralModifier = bearingsLength > 1 || bearingsLength === 0 ? 's' : '';
			toast.success(`${ exceptionSuccessMessage }${ bearingsLength } bearing${ pluralModifier } added to the project`, {
				id: toastId,
				duration: 5000,
			});
			setIsLoading(false);
			refreshProjects();
			resetSelectedBearings();
		} catch (error) {
			handleSaveErrors(error);
		}
	};

	const handleSaveErrors = (error: Error) => {
		setIsLoading(false);
		toast.dismiss();
		toast.error(error.message);
	};

	const handleSaveBearingsClick = (event: React.MouseEvent<HTMLElement>, projectId: string) => {
		event.preventDefault();
		saveDropdownSelectionAnalyticsHandler(event, dropdownRef, 'Add to Project', 'bearing part');
		saveMultiBearingsToProject(projectId);
		setIsOpen(false);
	};

	// not render anything if hideDropdown is true
	if (hideDropdown) {
		return null;
	}

	return (
		<div className={`dropdown ${ isLoading ? 't-full-block-skeleton' : '' } t-dropdown-list--${ menuDirection }`} ref={dropdownRef} data-item-number={buildBearingsListString(bearings)} data-project-count={projects.length}>
			<ToggleButton
				buttonStyle={buttonStyle}
				isOpen={isOpen}
				isLoggedIn={authenticated}
				inactive={inactive}
				marketPlaceDescription={'test'}
				toggleMenu={toggleMenu}
			/>
			<SaveToProjectDropDown
				projects={projects}
				isOpen={isOpen}
				targetType='bearings'
				onSuccess={onNewProjectCreated}
				onModalOpen={trackSaveDropdownSelection}
				projectItemCompareValue={''}
				onSaveClick={handleSaveBearingsClick}>
			</SaveToProjectDropDown>
		</div>
	);
};

export default SaveMultiBearingDropdown;
